
import declare from "dojo/_base/declare";
import ActionController from "csi/action/Controller";
import locale from "csi/locale";
import csi from "csi/main";
import MessageDialog from "csi/widget/MessageDialog";
import util from "csi/util";
import Response from "csi/comm/Response";

const COMPLIANCE_ANALYSIS_POPUP_TITLE = "ComplianceAnalysisPopupTitle";
const COMPLIANCE_ANALYSIS_SUCCESS_MESSAGE = "ComplianceAnalysisSuccessMessage";

const FoodLabelComplianceAnalysisActionController = declare(ActionController, {

	bypassConfirmed: false,

	// Show progress dialog when performing action
	bShowProgress: true,

	/** @property {Number} nStatusLoadingMinDelay - minimum time the loading popup needs to remain open in milliseconds */
	nStatusLoadingMinDelay: 1000,

	/** @property {Number} _nStatusLoadingStart - time when loading popup was created in milliseconds */
	_nStatusLoadingStart: null,

	/**
	 * Wait until loading popup has been open for the minimum amount of time
	 */
	async waitForLoadPopup() {
		const loadingTime = Date.now() - this._nStatusLoadingStart;
		const remainingTime = Math.max(this.nStatusLoadingMinDelay - loadingTime, 0);

		await util.wait(remainingTime);
	},
	
	/**
	 * @override
	 * Display progress dialog when action is performed
	 */
	waitForPublish() {
		this._nStatusLoadingStart = Date.now();
		this.inherited(arguments);
	},

	/**
	 * @override
	 * Display error dialog if operation has error
	 * @param err 
	 */
	async _onPublishError(/*Error*/err) {
		await this.waitForLoadPopup();
		this.displayErrorDialog({
			materialIcon: MessageDialog.ICON_ERROR,
			message: err.message,
			title: locale.resource(COMPLIANCE_ANALYSIS_POPUP_TITLE),
		});
	},

	/**
	 * @override
	 * Display a warning popup if any Flat Ingredeint Items have bypass enabled
	 */
	process(): void {
		const self = this;

		// Submit the request when no Flat Ingredient Items have bypass enabled or when the user clicks "Confirm" on the bypass warning dialog
		if (!this.hasBypassedItems() || this.bypassConfirmed) {
			return this.inherited<void>(arguments);
		}

		// Prompt the user to send the request despite bypassed Flat Ingredient Items
		new MessageDialog({
			buttons: [MessageDialog.BUTTON_CONFIRM + MessageDialog.BUTTON_PRIMARY, MessageDialog.BUTTON_CANCEL],
			materialIcon: MessageDialog.ICON_WARNING,
			message: locale.resource("ComplianceAnalysisBypassWarningPopupMessage"),
			title: locale.resource("ComplianceAnalysisBypassWarningPopupTitle"),
			onResponse: function(key, _): void {
				// Call the process method again to submit the request
				if (key == MessageDialog.BUTTON_CONFIRM) {
					self.bypassConfirmed = true;
					self.process();
				}
				// Close both the warning popup and form dialog
				else if (key == MessageDialog.BUTTON_CANCEL) {
					self.postProcess();
					self.closeDialog();
				}
			},
		}).show();

		return null;
	},

	/*
	 * Check if any of the Flat Ingredient Items need to be bypassed and are not obsoleted
	 */
	hasBypassedItems(): Boolean {
		const cache = this.getCache();
		const flatIngredientUrlList = cache.getNode(this.getParentURL()).UniqueFlatIngredients;
		return flatIngredientUrlList.some((url) => {
			const flatIngNode = cache.getNode(url);
			const hasBypassReason = !!flatIngNode.ProductComplianceAnalysisBypassReason;
			const isObsoleted = flatIngNode.CalculationStatus === "LabelItemCalculationStatus:Obsoleted";
			return hasBypassReason && !isObsoleted;
		});
	},

	/**
	 * @override
	 * Wait for loading popup if it hasn't been displayed for long enough
	 */
	async _onPublishResponse(response: Response) {
		await this.waitForLoadPopup();
		this.publishResponse(response);
	},
	
	/**
	 * @override 
	 * Add success message based on operation response values
	 */
	postProcessSuccess() {
		new MessageDialog({
			buttons: [MessageDialog.BUTTON_OK],
			materialIcon: MessageDialog.ICON_COMPLETED,
			message: locale.resource(COMPLIANCE_ANALYSIS_SUCCESS_MESSAGE),
			title: locale.resource(COMPLIANCE_ANALYSIS_POPUP_TITLE),
		}).show();
	},
});

export = FoodLabelComplianceAnalysisActionController;
